import React, { useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Select from 'react-select';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import Tooltip from '@/components/ui-bridge/core/Tooltip';
import ReportOptions from '@/components/ReportOptions/ReportOptions';
import {
  SquareIcon,
  ExportIcon,
  PdfIcon,
  EditIcon,
  TrashIcon,
  CancelIcon,
  ArchiveIcon,
  UnarchiveIcon,
} from '@/components/ui-bridge/icons/ActionIcons';

import { useUser } from '@/contexts/UserContext';
import { hasFranchiseeActionAccess } from '@/helpers/orderPermissions';

const iconStyle = 'w-5 h-5 min-w-[20px] min-h-[20px] mr-1 flex-shrink-0 inline-block';

const ActionsMenu = ({
  actionHandler,
  checkAll,
  checked,
  currentPage,
  disableCheckbox,
  multipleArchiveAllowed,
  newOrderAction,
  orderId,
  orderStatus,
  orderType,
  flags,
  isArchived,
  orderByDir,
  changeOrderByDir,
  isFromTheSameFranchisee,
  selectedOrder,
}) => {
  const intl = useIntl();
  const user = useUser();
  const [open, setOpen] = useState(false);
  const reportButtonRef = useRef(null);

  const orderTypeConfigurations = useMemo(() => {
    try {
      const configurationString = window.sessionStorage.getItem('orderTypeConfigurations');
      return configurationString ? JSON.parse(configurationString) : null;
    } catch (e) {
      console.error('Failed to parse orderTypeConfigurations', e);
      return null;
    }
  }, []);

  const handleClick = () => {
    setOpen(prev => !prev);
  };

  const closeReportOptionsHandler = event => {
    if (event !== undefined && reportButtonRef.current?.contains(event.target)) return;
    setOpen(false);
  };

  const deleteHandler = event => {
    const message = intl.formatMessage({ id: 'MESSAGE.DELETE_CONFIRMATION' });
    if (window.confirm(message)) {
      actionHandler('openDeleteOrder');
    }
  };

  const sortOptions = [
    {
      value: 'desc',
      label: intl.formatMessage({ id: 'SORT.MOST_RECENT' }, { defaultMessage: 'Mais recente primeiro' }),
    },
    { value: 'asc', label: intl.formatMessage({ id: 'SORT.OLDEST' }, { defaultMessage: 'Mais antigo primeiro' }) },
  ];

  let parentOrderType = orderType;
  if (orderType?.startsWith('marketplace_')) {
    parentOrderType = parentOrderType.replace('marketplace_', '');
  }

  let configurations = {
    canBeArchived: multipleArchiveAllowed,
  };

  if (orderTypeConfigurations?.[parentOrderType]?.[orderStatus]) {
    configurations = orderTypeConfigurations[parentOrderType][orderStatus];
  }

  const ActionBtn = ({ icon, labelId, onClick, disabled, dataCy, titleId, className, buttonRef }) => {
    const label = labelId ? <FormattedMessage id={labelId} /> : null;
    const title = titleId ? <FormattedMessage id={titleId} /> : label;

    return (
      <Tooltip title={title} placement="bottom">
        <button
          ref={buttonRef}
          type="button"
          disabled={disabled}
          onClick={onClick}
          data-cy={dataCy}
          className={clsx(
            'inline-flex items-center justify-center rounded border bg-white',
            'h-[2vw] min-h-[32px] min-w-[64px] ml-[0.5vw] px-[7px] py-[5px]',
            'text-[0.75rem] font-semibold leading-none transition-colors duration-300',
            'font-[Open_Sans,sans-serif]',
            disabled
              ? 'cursor-default text-black/26 border-black/12 pointer-events-none'
              : 'cursor-pointer border-[#D1D1D1] text-[#212529] hover:bg-[#f5f5f5] active:bg-[#e8e8e8]',
            className
          )}
        >
          {icon}
          {label}
        </button>
      </Tooltip>
    );
  };

  return (
    <div className="ml-[0.5vw] mt-[1vw] pb-[10px]">
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center flex-grow flex-wrap text-[12px] gap-2">
          <ActionBtn
            icon={<SquareIcon checked={checked} className={iconStyle} />}
            onClick={e => checkAll(e, checked ? 'uncheck' : 'check')}
            disabled={disableCheckbox}
            dataCy="select-all-orders-button"
            titleId="ACTIONS_MENU.SELECT_ALL"
            className={checked ? 'text-[#5882C1]' : 'text-[#8b8f97]'}
          />

          <ActionBtn
            icon={<ExportIcon className={iconStyle} />}
            labelId="ACTIONS_MENU.EXPORT"
            onClick={() => actionHandler('generatePhotos')}
            disabled={!configurations.canBeExported}
            dataCy="export-photos-button"
          />

          <ActionBtn
            icon={<PdfIcon className={iconStyle} />}
            labelId="ACTIONS_MENU.GENERATE_PDF"
            onClick={handleClick}
            disabled={!configurations.canGenerateReport}
            dataCy="export-report-button"
            buttonRef={reportButtonRef}
          />

          <ActionBtn
            icon={<EditIcon className={iconStyle} />}
            labelId="ACTIONS_MENU.EDIT"
            onClick={() => actionHandler('openEditOrder')}
            disabled={!configurations.canBeEdited || (!isFromTheSameFranchisee && !hasFranchiseeActionAccess(user, selectedOrder))}
            dataCy="edit-order-button"
          />

          {currentPage === 'independent' ? (
            <ActionBtn
              icon={<TrashIcon className={iconStyle} />}
              labelId="ACTIONS_MENU.DELETE"
              onClick={e => deleteHandler(e)}
              disabled={!configurations.canBeDeleted || !isFromTheSameFranchisee}
              dataCy="delete-order-button"
            />
          ) : (
            <ActionBtn
              icon={<CancelIcon className={iconStyle} />}
              labelId="ACTIONS_MENU.CANCEL"
              onClick={() => actionHandler('openCancelOrder')}
              disabled={!configurations.canBeCanceled || !isFromTheSameFranchisee}
              dataCy="cancel-order-button"
            />
          )}

          {currentPage === 'archived' || isArchived ? (
            <ActionBtn
              icon={<UnarchiveIcon className={iconStyle} />}
              labelId="ACTIONS_MENU.UNARCHIVE"
              onClick={() => actionHandler('unarchiveOrder')}
              disabled={!configurations.canBeArchived || orderStatus === 'FINISHED' || !isFromTheSameFranchisee}
              dataCy="unarchive-order-button"
            />
          ) : (
            <ActionBtn
              icon={<ArchiveIcon className={iconStyle} />}
              labelId="ACTIONS_MENU.ARCHIVE"
              onClick={() => actionHandler('archiveOrder')}
              disabled={!configurations.canBeArchived || orderStatus === 'FINISHED' || !isFromTheSameFranchisee}
              dataCy="archive-order-button"
            />
          )}

          <div className="ml-[0.5vw]">
            <Select
              value={sortOptions?.find(option => option.value === orderByDir)}
              options={sortOptions}
              onChange={option => changeOrderByDir(option)}
              isSearchable={false}
              className="min-w-[175px]"
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPosition="fixed"
              menuPlacement="auto"
              styles={{
                control: base => ({
                  ...base,
                  fontSize: '12px',
                  minHeight: '32px',
                  height: '32px',
                  maxHeight: '38px',
                  borderRadius: '4px',
                  borderColor: '#D1D1D1',
                  boxShadow: 'none',
                  outline: 'none',
                }),
                dropdownIndicator: base => ({ ...base, padding: '6px' }),
                option: base => ({ ...base, fontSize: '12px' }),
                menuPortal: base => ({ ...base, zIndex: 9999 }),
                menu: base => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>
        </div>

        <div className="flex-shrink-0 text-right pr-2">
          <button
            type="button"
            onClick={newOrderAction}
            className={clsx(
              'flex h-9 min-h-[36px] items-center justify-center rounded px-6 transition-all',
              "bg-[#5882C1] text-white font-normal text-sm tracking-wide font-['Open_Sans'] mr-4 border-none",
              'hover:bg-[#202F4A] active:bg-[#1a263d]'
            )}
            data-cy="new-order-button"
          >
            <FormattedMessage id="ACTIONS_MENU.NEW_ORDER" />
          </button>
        </div>
      </div>

      {configurations.reportsOptions && orderId && (
        <ReportOptions
          show={open}
          closeHandler={closeReportOptionsHandler}
          anchorEl={() => reportButtonRef.current}
          options={configurations.reportsOptions}
          entityId={orderId}
          flags={flags}
        />
      )}
    </div>
  );
};

ActionsMenu.propTypes = {
  checkAll: PropTypes.func.isRequired,
  newOrderAction: PropTypes.func.isRequired,
  checked: PropTypes.bool.isRequired,
  disableCheckbox: PropTypes.bool.isRequired,
  currentPage: PropTypes.string,
  actionHandler: PropTypes.func.isRequired,
  multipleArchiveAllowed: PropTypes.bool.isRequired,
  orderType: PropTypes.string,
  orderId: PropTypes.string,
  orderStatus: PropTypes.string,
  flags: PropTypes.object,
  isArchived: PropTypes.bool.isRequired,
  changeOrderByDir: PropTypes.func.isRequired,
  orderByDir: PropTypes.string.isRequired,
  isFromTheSameFranchisee: PropTypes.bool.isRequired,
};

ActionsMenu.defaultProps = {
  currentPage: null,
  orderId: undefined,
  orderType: undefined,
  orderStatus: undefined,
  flags: null,
};

export default ActionsMenu;
